/**
 * Copyright (c) Meta Platforms, Inc. and affiliates.
 *
 * This source code is licensed under the MIT license found in the
 * LICENSE file in the root directory of this source tree.
 *
 */

import { useLexicalComposerContext } from "@lexical/react/LexicalComposerContext";
import { useLexicalEditable } from "@lexical/react/useLexicalEditable";
import { $getTableAndElementByKey, $isTableCellNode, $isTableNode, TableNode } from "@lexical/table";
import { $findMatchingParent, mergeRegister } from "@lexical/utils";
import { debounce } from "@mui/material";
import { $getNearestNodeFromDOMNode, EditorThemeClasses, isHTMLElement, NodeKey } from "lexical";
import type { JSX } from "react";
import * as React from "react";
import { useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";

export function getThemeSelector(
  getTheme: () => EditorThemeClasses | null | undefined,
  name: keyof EditorThemeClasses
): string {
  const className = getTheme()?.[name];
  if (typeof className !== "string") {
    throw new Error(`getThemeClass: required theme property ${name} not defined`);
  }
  return className
    .split(/\s+/g)
    .map((cls) => `.${cls}`)
    .join();
}

function TableHoverActionsContainer(): JSX.Element | null {
  const [editor, { getTheme }] = useLexicalComposerContext();
  const isEditable = useLexicalEditable();
  const [shouldListenMouseMove, setShouldListenMouseMove] = useState<boolean>(false);
  const tableSetRef = useRef<Set<NodeKey>>(new Set());
  const tableCellDOMNodeRef = useRef<HTMLElement | null>(null);

  const debouncedOnMouseMove = debounce((event: MouseEvent) => {
    const { isOutside, tableDOMNode } = getMouseInfo(event, getTheme);

    if (isOutside) {
      return;
    }

    if (!tableDOMNode) {
      return;
    }

    tableCellDOMNodeRef.current = tableDOMNode;

    editor.getEditorState().read(
      () => {
        const maybeTableCell = $getNearestNodeFromDOMNode(tableDOMNode);

        if ($isTableCellNode(maybeTableCell)) {
          const table = $findMatchingParent(maybeTableCell, (node) => $isTableNode(node));
          if (!$isTableNode(table)) {
            return;
          }
        }
      },
      { editor }
    );
  }, 50);

  // Hide the buttons on any table dimensions change to prevent last row cells
  // overlap behind the 'Add Row' button when text entry changes cell height
  const tableResizeObserver = useMemo(() => new ResizeObserver(() => {}), []);

  useEffect(() => {
    if (!shouldListenMouseMove) {
      return;
    }

    document.addEventListener("mousemove", debouncedOnMouseMove);

    return () => {
      debouncedOnMouseMove.clear();
      document.removeEventListener("mousemove", debouncedOnMouseMove);
    };
  }, [shouldListenMouseMove, debouncedOnMouseMove]);

  useEffect(
    () =>
      mergeRegister(
        editor.registerMutationListener(
          TableNode,
          (mutations) => {
            editor.getEditorState().read(
              () => {
                let resetObserver = false;
                for (const [key, type] of mutations) {
                  switch (type) {
                    case "created": {
                      tableSetRef.current.add(key);
                      resetObserver = true;
                      break;
                    }
                    case "destroyed": {
                      tableSetRef.current.delete(key);
                      resetObserver = true;
                      break;
                    }
                    default:
                      break;
                  }
                }
                if (resetObserver) {
                  // Reset resize observers
                  tableResizeObserver.disconnect();
                  for (const tableKey of tableSetRef.current) {
                    const { tableElement } = $getTableAndElementByKey(tableKey);
                    tableResizeObserver.observe(tableElement);
                  }
                  setShouldListenMouseMove(tableSetRef.current.size > 0);
                }
              },
              { editor }
            );
          },
          { skipInitialization: false }
        )
      ),
    [editor, tableResizeObserver]
  );

  if (!isEditable) {
    return null;
  }

  return <></>;
}

function getMouseInfo(
  event: MouseEvent,
  getTheme: () => EditorThemeClasses | null | undefined
): {
  tableDOMNode: HTMLElement | null;
  isOutside: boolean;
} {
  const target = event.target;
  const tableCellClass = getThemeSelector(getTheme, "tableCell");

  if (isHTMLElement(target)) {
    const tableDOMNode = target.closest<HTMLElement>(`td${tableCellClass}, th${tableCellClass}`);

    const isOutside = !(tableDOMNode || target.closest<HTMLElement>("div.TableCellResizer__resizer"));

    return { isOutside, tableDOMNode };
  } else {
    return { isOutside: true, tableDOMNode: null };
  }
}

export function TableHoverActionsPlugin({
  anchorElem = document.body,
}: {
  anchorElem?: HTMLElement;
}): React.ReactNode | null {
  const isEditable = useLexicalEditable();

  return <>{isEditable ? createPortal(<TableHoverActionsContainer />, anchorElem) : null}</>;
}
